'use client'

import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, Link, Text } from '@chakra-ui/react'
import Image from 'next/image'
import { usePathname, useSearchParams } from 'next/navigation'

import { CustomerInfo } from 'app/utils/authentication'
import { CartPreview } from 'components/nav/CartPreview'
import { StyledMenuButton } from 'components/nav/MenuButton/styles'
import { AppDispatch } from 'store'
import { selectOrderState } from 'store/partials/cart/cartSelectors'
import { getCart, resetCart } from 'store/partials/cart/cartSlice'
import { STORAGE_KEYS } from 'utils/constants'
import { parseRouterQuery } from 'utils/parseRouterQuery'
import { getFromStorage } from 'utils/storage'

import Menu from '../Menu'

type Props = {
  customerInfo: CustomerInfo
}

const CartAndMenuButton = ({ customerInfo }: Props): JSX.Element => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const dispatch = useDispatch<AppDispatch>()
  const { cart } = useSelector(selectOrderState)

  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false)

  useEffect(() => {
    const orderId = getFromStorage(STORAGE_KEYS.orderId)

    const isProlongation = parseRouterQuery(searchParams?.get('prolongation_id') ?? undefined)
    const pattern = new RegExp(`(\/payments\/*.*\/success)`)
    const isOnPaymentPage = pattern.test(pathname ?? '')
    const shouldResetCart = isOnPaymentPage && orderId && !isProlongation

    if (shouldResetCart) {
      dispatch(resetCart())
    } else if (orderId && !cart) {
      dispatch(getCart(Number(orderId)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, searchParams, pathname])

  return (
    <>
      <Box position="relative" marginRight="10px" marginLeft="26px">
        {cart?.orderItems && cart?.orderItems.length >= 1 && (
          <Flex
            width="18px"
            height="18px"
            backgroundColor="green.a200"
            position="absolute"
            zIndex={2}
            right="0"
            alignItems="center"
            justifyContent="center"
            borderRadius="100%"
          >
            <Text lineHeight="1" fontSize="xs" color="white" margin="0" padding="0">
              {cart?.orderItems.length}
            </Text>
          </Flex>
        )}
        <Link href="/cart" _focus={{ boxShadow: 'none' }}>
          <StyledMenuButton
            onMouseEnter={() => setIsCartPreviewOpen(true)}
            onMouseLeave={() => setIsCartPreviewOpen(false)}
            isOpen={isCartPreviewOpen}
          >
            <Image src="/icons/cart.svg" alt="Cart button icon" width={20} height={20} loading="eager" />
          </StyledMenuButton>
        </Link>
        {isCartPreviewOpen && <CartPreview />}
      </Box>

      <Menu
        isLoggedIn={customerInfo.customer !== null}
        isCartPreviewOpen={isCartPreviewOpen}
        customerInfo={customerInfo}
      />
    </>
  )
}

export default CartAndMenuButton
